import type { FC, MouseEvent } from "react";
import { useState } from "react";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Popover,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Check from "@mui/icons-material/Check";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { SearchBox } from "@foodpilot/foods";

import { canDrillDownCpc, useCpcList } from "src/api/sure-api/cpc/useCpcList";
import { type Cpc } from "src/api/sure-api/cpc/useCpcSelector";

export const formatCpcLabel = (cpc: Cpc): string => `${cpc.code} — ${cpc.title}`;

type PathSegment = {
    code: string;
    title: string;
};

interface CpcHierarchyPickerProps {
    value: Cpc | null;
    onChange: (option: Cpc | null) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: object;
}

export const CpcHierarchyPicker: FC<CpcHierarchyPickerProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    helperText,
    sx = {},
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [path, setPath] = useState<PathSegment[]>([]);
    const [search, setSearch] = useState("");

    const isOpen = Boolean(anchorEl);
    const parentCode = path.length > 0 ? path[path.length - 1].code : null;
    const trimmedSearch = search.trim();

    const { cpcItems, isLoading, isSearchMode } = useCpcList({
        parentCode,
        searchText: trimmedSearch,
        enabled: isOpen,
    });

    const handleClose = () => {
        setAnchorEl(null);
        setPath([]);
        setSearch("");
    };

    const handleSelect = (cpc: Cpc) => {
        onChange(cpc);
        handleClose();
    };

    const handleDrill = (cpc: Cpc) => {
        if (!canDrillDownCpc(cpc)) {
            handleSelect(cpc);
            return;
        }

        setPath((current) => [...current, { code: cpc.code, title: cpc.title }]);
        setSearch("");
    };

    const handleBreadcrumbNavigate = (index: number) => {
        if (index < 0) {
            setPath([]);
        } else {
            setPath((current) => current.slice(0, index + 1));
        }
        setSearch("");
    };

    const handleSelectClick = (event: MouseEvent, cpc: Cpc) => {
        event.stopPropagation();
        handleSelect(cpc);
    };

    const displayValue = value ? formatCpcLabel(value) : "";

    return (
        <>
            <TextField
                value={displayValue}
                placeholder={
                    placeholder ?? t("cpc.select.placeholder", "Browse CPC categories or search by English label")
                }
                variant="outlined"
                error={error}
                helperText={helperText}
                disabled={disabled}
                onClick={(event) => {
                    if (!disabled) {
                        setAnchorEl(event.currentTarget);
                    }
                }}
                InputProps={{
                    readOnly: true,
                    sx: { cursor: disabled ? "default" : "pointer" },
                }}
                sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "24px",
                        backgroundColor: "#FFFFFF",
                        minHeight: "40px",
                        "& fieldset": {
                            borderColor: "#E7E6E4",
                        },
                        "&:hover fieldset": {
                            borderColor: "#D0D0D0",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                        },
                    },
                    "& .MuiOutlinedInput-input": {
                        fontSize: "0.875rem",
                        padding: "0px 16px !important",
                        color: "#393A36",
                        cursor: disabled ? "default" : "pointer",
                        "&::placeholder": {
                            color: "#A3A2A0",
                            opacity: 1,
                        },
                    },
                    ...sx,
                }}
            />

            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: {
                        sx: {
                            width: anchorEl?.clientWidth ?? 420,
                            minWidth: 360,
                            maxWidth: 560,
                            mt: 0.5,
                        },
                    },
                }}
            >
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {path.length > 0 && !isSearchMode && (
                            <IconButton
                                size="small"
                                aria-label={t("common.back", "Back")}
                                onClick={() => handleBreadcrumbNavigate(path.length - 2)}
                            >
                                <ArrowBack fontSize="small" />
                            </IconButton>
                        )}
                        <Breadcrumbs
                            aria-label={t("cpc.select.breadcrumb", "CPC navigation")}
                            sx={{ flex: 1, "& .MuiBreadcrumbs-li": { maxWidth: 180 } }}
                        >
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                color={path.length === 0 && !isSearchMode ? "text.primary" : "inherit"}
                                onClick={() => handleBreadcrumbNavigate(-1)}
                                sx={{
                                    border: 0,
                                    background: "none",
                                    cursor: "pointer",
                                    fontSize: "0.8125rem",
                                    textAlign: "left",
                                }}
                            >
                                {t("cpc.select.root", "All categories")}
                            </Link>
                            {!isSearchMode &&
                                path.map((segment, index) => (
                                    <Link
                                        key={segment.code}
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        color={index === path.length - 1 ? "text.primary" : "inherit"}
                                        onClick={() => handleBreadcrumbNavigate(index)}
                                        sx={{
                                            border: 0,
                                            background: "none",
                                            cursor: "pointer",
                                            fontSize: "0.8125rem",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {segment.code} — {segment.title}
                                    </Link>
                                ))}
                            {isSearchMode && (
                                <Typography variant="body2" color="text.secondary">
                                    {t("common.search", "Search")}
                                </Typography>
                            )}
                        </Breadcrumbs>
                    </Box>

                    <SearchBox value={search} onChange={setSearch} />

                    <List
                        dense
                        sx={{
                            maxHeight: 320,
                            overflowY: "auto",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            py: 0,
                        }}
                    >
                        {isLoading && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {!isLoading && cpcItems.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                                {isSearchMode ?
                                    t("common.noResults", "No results")
                                :   t("cpc.select.noChildren", "No subcategories")}
                            </Typography>
                        )}

                        {!isLoading &&
                            cpcItems.map((cpc) => {
                                const isSelected = value?.code === cpc.code;
                                const drillable = canDrillDownCpc(cpc) && !isSearchMode;

                                return (
                                    <ListItem
                                        key={cpc.code}
                                        disablePadding
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                aria-label={t("cpc.select.choose", "Select this CPC code")}
                                                onClick={(event) => handleSelectClick(event, cpc)}
                                                color={isSelected ? "primary" : "default"}
                                            >
                                                <Check fontSize="small" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemButton
                                            onClick={() => (drillable ? handleDrill(cpc) : handleSelect(cpc))}
                                            selected={isSelected}
                                            sx={{ pr: 6 }}
                                        >
                                            <Chip
                                                label={cpc.code}
                                                size="small"
                                                sx={{ height: 20, fontSize: "0.7rem", mr: 1, flexShrink: 0 }}
                                            />
                                            <ListItemText
                                                primary={cpc.title}
                                                primaryTypographyProps={{
                                                    variant: "body2",
                                                    sx: { overflow: "hidden", textOverflow: "ellipsis" },
                                                }}
                                            />
                                            {drillable && (
                                                <ChevronRight fontSize="small" sx={{ ml: 0.5, flexShrink: 0 }} />
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                    </List>

                    <Typography variant="caption" color="text.secondary">
                        {t(
                            "cpc.select.hint",
                            "Browse top-level categories, then drill down. Labels are in English. Use the check icon to select a category at any level.",
                        )}
                    </Typography>
                </Box>
            </Popover>
        </>
    );
};
